import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { trackFinanceEnquiry } from "@/lib/analytics";
import {
  COMPANY_MAX_CHARS,
  NAME_MAX_CHARS,
  PHONE_MAX_CHARS,
  formatPhoneAsTyped,
  stripDigitsForName,
  validateCompany,
  validateEmail,
  validateName,
  validatePhone,
} from "@/lib/validators";

import {
  ChoiceCards,
  ChoiceChecks,
  ChoiceSelect,
  ConsentBox,
  Field,
  TextField,
} from "./ScopingFields";
import { ScopingSuccess } from "./ScopingSuccess";
import { submitEnquiry, useScopingOptions } from "./useScopingOptions";

const SLUG = "outsourced-financial-management";
const PAYMENTS = "supplier_payments";
const OTHER_INDUSTRY = "other";
const OTHER_INDUSTRY_MAX = 80;
const MESSAGE_MAX_CHARS = 2000;

export const SCOPING_CONSENT =
  "I'm happy for T-TITE to use these details to scope and discuss an outsourced finance " +
  "function for my business. I understand my information is stored securely and will never " +
  "be sold or shared with third parties.";

/**
 * The answers that mean somebody genuinely needs a finance function rather
 * than a bookkeeper. A smaller business that picks any of these is in the
 * right place; one that picks none of them may be better served - and better
 * priced - by the bookkeeping service.
 */
const MANAGEMENT_CODES = [
  "management_accounts",
  "cash_flow",
  "budgets",
  "kpi_reporting",
  "board_reporting",
  "audit_coordination",
];

const STEPS = [
  { key: "business", label: "Your business" },
  { key: "needs", label: "What you need" },
  { key: "contact", label: "Your details" },
  { key: "review", label: "Review & send" },
];

const EMPTY = {
  revenue_band: "",
  industry: "",
  industry_other: "",
  entities: "",
  finance_staff: "",
  responsibilities: [],
  support_level: "",
  payment_volume: "",
  payment_frequency: "",
  full_name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  consent: false,
  website_url: "",
};

/** Which answers each step is responsible for, so "Continue" can check them. */
const REQUIRED = {
  business: ["revenue_band", "industry", "entities", "finance_staff"],
  needs: ["responsibilities", "support_level"],
  contact: ["full_name", "company", "email", "phone", "consent"],
  review: [],
};

const MISSING = {
  revenue_band: "Please choose the range your annual revenue falls into.",
  industry: "Please tell us which industry best describes your business.",
  industry_other:
    "Please tell us what your business does, so we know which kind of finance work it brings.",
  entities: "Please tell us how many entities need finance support.",
  finance_staff: "Please tell us what finance staff you have today.",
  responsibilities: "Please choose at least one area you'd like us to take on.",
  support_level: "Please tell us roughly how much support you need.",
  payment_volume: "Please tell us roughly how many payments you run.",
  payment_frequency: "Please tell us how often payment runs happen.",
  consent:
    "Please tick the box to confirm you're happy for us to use these details to scope your " +
    "finance function.",
};

const isBlank = (value) =>
  Array.isArray(value) ? value.length === 0 : !String(value || "").trim();

/**
 * The scoping brief.
 *
 * Four short steps instead of one long form: the questions that change the
 * team (revenue, industry, entities, existing staff) are easier to answer
 * before anybody is asked for contact details, and nothing is sent until the
 * whole brief has been read back. Choosing supplier payments opens the two
 * follow-ups that genuinely move the workload.
 */
export const ScopingForm = () => {
  const { options, failed } = useScopingOptions();
  const { search } = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  // Somebody sent here from the calculator already told us their turnover, so
  // the revenue question arrives answered rather than asked twice.
  const handedOver = new URLSearchParams(search).get("revenue") || "";
  const [answers, setAnswers] = useState(() =>
    (handedOver ? { ...EMPTY, revenue_band: handedOver } : EMPTY));
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const fromCalculator = Boolean(handedOver) && answers.revenue_band === handedOver;

  const step = STEPS[stepIndex];
  const wantsPayments = answers.responsibilities.includes(PAYMENTS);
  const wantsOtherIndustry = answers.industry === OTHER_INDUSTRY;

  /**
   * A smaller business asking only for processing and compliance is pointed at
   * the bookkeeping service - as a suggestion, never a gate. Revenue alone
   * decides nothing: ask for cash flow, budgets or board reporting at R15m and
   * you belong here.
   */
  const maybeBookkeeping =
    answers.revenue_band === "under_r20m" &&
    answers.responsibilities.length > 0 &&
    !answers.responsibilities.some((code) => MANAGEMENT_CODES.includes(code));

  const set = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  /** A person's name takes letters only - digits are dropped as they're typed. */
  const setTypedName = (event) => set("full_name", stripDigitsForName(event.target.value));

  /** Phone spaces itself out as "081 788 2993" and stops at 12 characters. */
  const setTypedPhone = (event) => set("phone", formatPhoneAsTyped(event.target.value));

  const togglePick = (code) =>
    setAnswers((prev) => {
      const next = prev.responsibilities.includes(code)
        ? prev.responsibilities.filter((item) => item !== code)
        : [...prev.responsibilities, code];
      return { ...prev, responsibilities: next };
    });

  const fieldsFor = (key) => {
    const fields = [...REQUIRED[key]];
    if (key === "business" && wantsOtherIndustry) fields.push("industry_other");
    if (key === "needs" && wantsPayments) {
      fields.push("payment_volume", "payment_frequency");
    }
    return fields;
  };

  /**
   * Contact details are held to exactly the rules the calculator uses, so a
   * typo is caught here rather than coming back from the server.
   */
  const contactProblems = () => {
    const found = {};
    const name = validateName(answers.full_name, "your name");
    if (name) found.full_name = name;
    const company = validateCompany(answers.company);
    if (company) found.company = company;
    const email = validateEmail(answers.email);
    if (email) found.email = email;
    const phone = validatePhone(answers.phone);
    if (phone) found.phone = phone;
    if (!answers.consent) found.consent = MISSING.consent;
    return found;
  };

  const problemsIn = (key) => {
    if (key === "contact") return contactProblems();
    const found = {};
    fieldsFor(key).forEach((field) => {
      if (isBlank(answers[field])) found[field] = MISSING[field];
    });
    return found;
  };

  const checkStep = (key) => {
    const found = problemsIn(key);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`scoping-${first}`)?.focus();
      return false;
    }
    return true;
  };

  const next = () => {
    if (!checkStep(step.key)) return;
    setErrors({});
    setStepIndex((index) => Math.min(index + 1, STEPS.length - 1));
  };

  const back = () => {
    setErrors({});
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const editStep = (index) => {
    setErrors({});
    setStepIndex(index);
  };

  const send = async () => {
    // Read the whole brief once more before anything leaves the browser: the
    // review step is the first place somebody can send it, and they may have
    // edited an earlier answer on the way here.
    for (const [index, candidate] of STEPS.entries()) {
      const found = problemsIn(candidate.key);
      if (Object.keys(found).length) {
        setErrors(found);
        setStepIndex(index);
        toast.error("Please check the highlighted answers.");
        return;
      }
    }

    setSending(true);
    try {
      const reference = await submitEnquiry(answers);
      setDone(reference);
      /* A real enquiry: report it to Meta and Google Ads, consent permitting. */
      trackFinanceEnquiry(reference);
    } catch (problem) {
      if (problem.fieldErrors) {
        setErrors(problem.fieldErrors);
        toast.error("Please check the highlighted answers.");
        const stepWithProblem = STEPS.findIndex((candidate) =>
          fieldsFor(candidate.key).some((field) => problem.fieldErrors[field]),
        );
        if (stepWithProblem >= 0) setStepIndex(stepWithProblem);
      } else {
        toast.error("We couldn't send that just now. Please try again, or call us.");
      }
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return <ScopingSuccess reference={done} />;
  }

  if (failed) {
    return (
      <div data-testid="scoping-form-unavailable" className="tt-card p-6">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The form isn&apos;t loading at the moment. Please call us on 081 788 2993 or email
          info@t-tite.co.za and we&apos;ll scope it with you directly.
        </p>
      </div>
    );
  }

  if (!options) {
    return (
      <div data-testid="scoping-form-loading" className="tt-card space-y-3 p-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-2/3" />
      </div>
    );
  }

  const industryReading =
    wantsOtherIndustry && answers.industry_other.trim()
      ? `Other — ${answers.industry_other.trim()}`
      : options.industries[answers.industry] || "—";

  const reviewGroups = [
    {
      key: "business",
      heading: "Your business",
      rows: [
        ["Annual revenue", options.revenue_bands[answers.revenue_band]],
        ["Industry", industryReading],
        ["Entities", options.entity_counts[answers.entities]],
        ["Finance staff today", options.finance_staff[answers.finance_staff]],
      ],
    },
    {
      key: "needs",
      heading: "What you need",
      rows: [
        [
          "Areas for us to take on",
          answers.responsibilities.map((code) => options.responsibilities[code]).join(", "),
        ],
        ["Support wanted", options.support_levels[answers.support_level]],
        ...(wantsPayments
          ? [
              ["Payments a month", options.payment_volumes[answers.payment_volume]],
              ["Payment runs", options.payment_frequencies[answers.payment_frequency]],
            ]
          : []),
      ],
    },
    {
      key: "contact",
      heading: "Your details",
      rows: [
        ["Name", answers.full_name],
        ["Company", answers.company],
        ["Email", answers.email],
        ["Phone", answers.phone],
        ["Anything else", answers.message.trim() || "Nothing added"],
      ],
    },
  ];

  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <div data-testid="scoping-form" className="tt-card p-6 sm:p-8">
      {/* progress */}
      <ol className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {STEPS.map((candidate, index) => (
          <li key={candidate.key} className="flex items-center gap-3">
            <span
              data-testid={`scoping-step-${index + 1}`}
              data-current={index === stepIndex ? "true" : "false"}
              className={`text-[12px] font-bold uppercase tracking-[0.1em] ${
                index === stepIndex
                  ? "text-primary"
                  : index < stepIndex
                    ? "text-foreground"
                    : "text-muted-foreground/70"
              }`}
            >
              {String(index + 1).padStart(2, "0")} {candidate.label}
            </span>
            {index < STEPS.length - 1 ? (
              <span aria-hidden="true" className="h-px w-6 bg-border" />
            ) : null}
          </li>
        ))}
      </ol>

      {errorCount > 0 ? (
        <div
          role="alert"
          aria-live="assertive"
          data-testid="scoping-error-summary"
          className="mt-6 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Please fix {errorCount} {errorCount === 1 ? "answer" : "answers"} below and try again.
        </div>
      ) : null}

      <div className="mt-7 space-y-8">
        {step.key === "business" ? (
          <>
            {fromCalculator ? (
              <p
                data-testid="scoping-prefilled-note"
                className="rounded-xl border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]"
              >
                We&apos;ve carried your revenue across from the calculator. Change it below if
                it isn&apos;t right.
              </p>
            ) : null}

            <Field
              testId="scoping-revenue"
              label="Approximate annual revenue"
              error={errors.revenue_band}
            >
              <ChoiceCards
                name="revenue"
                options={options.revenue_bands}
                value={answers.revenue_band}
                onChange={(value) => set("revenue_band", value)}
              />
            </Field>

            <Field
              testId="scoping-industry"
              label="Which industry best describes your business?"
              error={errors.industry}
            >
              <ChoiceSelect
                name="industry"
                options={options.industries}
                value={answers.industry}
                placeholder="Choose an industry"
                invalid={Boolean(errors.industry)}
                onChange={(value) => set("industry", value)}
              />

              {wantsOtherIndustry ? (
                <div data-testid="scoping-industry-other-wrap" className="tt-fade-in mt-4">
                  <TextField
                    name="industry_other"
                    label="What does your business do?"
                    hint="A few words is plenty — for example “funeral services” or “solar installations”."
                    value={answers.industry_other}
                    onChange={(event) => set("industry_other", event.target.value)}
                    error={errors.industry_other}
                    maxLength={OTHER_INDUSTRY_MAX}
                    placeholder="Solar installations"
                  />
                </div>
              ) : null}
            </Field>

            <Field
              testId="scoping-entities"
              label="How many entities need finance support?"
              error={errors.entities}
            >
              <ChoiceCards
                name="entities"
                options={options.entity_counts}
                value={answers.entities}
                onChange={(value) => set("entities", value)}
              />
            </Field>

            <Field
              testId="scoping-staff"
              label="What finance staff do you have today?"
              error={errors.finance_staff}
            >
              <ChoiceCards
                name="staff"
                options={options.finance_staff}
                value={answers.finance_staff}
                onChange={(value) => set("finance_staff", value)}
                columns={1}
              />
            </Field>
          </>
        ) : null}

        {step.key === "needs" ? (
          <>
            <Field
              testId="scoping-scope"
              label="What would you like us to take on?"
              hint="Choose everything that applies. We'll confirm the detail when we scope it."
              error={errors.responsibilities}
            >
              <ChoiceChecks
                name="scope"
                options={options.responsibilities}
                values={answers.responsibilities}
                onToggle={togglePick}
              />
            </Field>

            {maybeBookkeeping ? (
              <div
                data-testid="scoping-bookkeeping-hint"
                className="tt-fade-in rounded-xl border border-[color:var(--tt-warn-border)] bg-[color:var(--tt-warn-bg)] p-5"
              >
                <p className="text-[13.5px] font-semibold text-[color:var(--tt-warn-text)]">
                  Your requirements may be better suited to our Bookkeeping &amp; Accounting
                  service.
                </p>
                <p className="mt-2 text-[13px] leading-relaxed text-[color:var(--tt-warn-text)]/90">
                  What you&apos;ve chosen is processing and compliance work, which that service
                  prices online. If you also need management accounts, cash-flow forecasting,
                  budgets or board reporting, add them above and carry on here.
                </p>
                <Link
                  to={`/bookkeeping-fees${search}`}
                  data-testid="scoping-bookkeeping-link"
                  className="tt-link mt-3 inline-flex text-[13.5px] font-semibold"
                >
                  View bookkeeping pricing
                </Link>
              </div>
            ) : null}

            <Field
              testId="scoping-support"
              label="How much support do you think you need?"
              hint="A rough idea is fine. If you're not sure, we'll recommend a structure."
              error={errors.support_level}
            >
              <ChoiceCards
                name="support"
                options={options.support_levels}
                value={answers.support_level}
                onChange={(value) => set("support_level", value)}
                columns={1}
              />
            </Field>

            {wantsPayments ? (
              <div data-testid="scoping-payment-questions" className="tt-fade-in space-y-8">
                <Field
                  testId="scoping-payment-volume"
                  label="Roughly how many supplier payments a month?"
                  error={errors.payment_volume}
                >
                  <ChoiceCards
                    name="payvol"
                    options={options.payment_volumes}
                    value={answers.payment_volume}
                    onChange={(value) => set("payment_volume", value)}
                  />
                </Field>

                <Field
                  testId="scoping-payment-frequency"
                  label="How often are payment runs performed?"
                  error={errors.payment_frequency}
                >
                  <ChoiceCards
                    name="payfreq"
                    options={options.payment_frequencies}
                    value={answers.payment_frequency}
                    onChange={(value) => set("payment_frequency", value)}
                  />
                </Field>
              </div>
            ) : null}
          </>
        ) : null}

        {step.key === "contact" ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <TextField
                name="full_name"
                label="Your name"
                value={answers.full_name}
                onChange={setTypedName}
                error={errors.full_name}
                autoComplete="name"
                maxLength={NAME_MAX_CHARS}
                placeholder="Thabo Mokoena"
              />
              <TextField
                name="company"
                label="Company / business name"
                value={answers.company}
                onChange={(event) => set("company", event.target.value)}
                error={errors.company}
                autoComplete="organization"
                maxLength={COMPANY_MAX_CHARS}
                placeholder="Mokoena Trading (Pty) Ltd"
              />
              <TextField
                name="email"
                label="Email address"
                type="email"
                inputMode="email"
                value={answers.email}
                onChange={(event) => set("email", event.target.value)}
                error={errors.email}
                autoComplete="email"
                maxLength={254}
                placeholder="you@yourbusiness.co.za"
              />
              <TextField
                name="phone"
                label="Phone number"
                type="tel"
                inputMode="tel"
                value={answers.phone}
                onChange={setTypedPhone}
                error={errors.phone}
                autoComplete="tel"
                maxLength={PHONE_MAX_CHARS}
                placeholder="081 788 2993"
              />
            </div>

            <div>
              <Label
                htmlFor="scoping-message"
                className="text-[13px] font-semibold text-foreground"
              >
                Anything else we should know?
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  (optional)
                </span>
              </Label>
              <Textarea
                id="scoping-message"
                data-testid="scoping-message"
                rows={4}
                maxLength={MESSAGE_MAX_CHARS}
                value={answers.message}
                onChange={(event) => set("message", event.target.value)}
                placeholder="Current pain points, timing, systems you use…"
                className="mt-2 bg-white"
              />
            </div>

            {/* honeypot: hidden from people, irresistible to bots */}
            <div className="tt-honeypot" aria-hidden="true">
              <label htmlFor="scoping-website">Website</label>
              <input
                id="scoping-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={answers.website_url}
                onChange={(event) => set("website_url", event.target.value)}
              />
            </div>

            <ConsentBox
              checked={answers.consent}
              onChange={(value) => set("consent", value)}
              error={errors.consent}
              text={SCOPING_CONSENT}
            />
          </div>
        ) : null}

        {step.key === "review" ? (
          <div data-testid="scoping-review" className="space-y-5">
            <p className="text-[14px] leading-relaxed text-muted-foreground">
              Please read your brief back before we send it. Anything can still be changed.
            </p>

            {reviewGroups.map((group, index) => (
              <div
                key={group.key}
                data-testid={`scoping-review-${group.key}`}
                className="rounded-xl border border-border bg-[color:var(--tt-surface-2)] p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-foreground">
                    {group.heading}
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editStep(index)}
                    data-testid={`scoping-review-edit-${group.key}`}
                    className="h-9 min-h-[36px] bg-white px-3 text-[13px] font-semibold"
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    Edit
                  </Button>
                </div>

                <dl className="mt-4 space-y-3">
                  {group.rows.map(([label, value]) => (
                    <div key={label} className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-4">
                      <dt className="text-[13px] text-muted-foreground">{label}</dt>
                      <dd className="text-[14px] font-medium leading-relaxed text-foreground sm:col-span-2">
                        {value || "—"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 sm:flex-row sm:items-center">
        {stepIndex > 0 ? (
          <Button
            type="button"
            variant="outline"
            onClick={back}
            data-testid={`${SLUG}-form-back`}
            className="h-12 min-h-[44px] bg-white px-5 text-[15px] font-semibold"
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back
          </Button>
        ) : null}

        {step.key === "review" ? (
          <Button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid={`${SLUG}-form-submit`}
            className="h-12 min-h-[44px] px-5 text-[15px] font-semibold"
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Sending…
              </>
            ) : (
              "Send my brief"
            )}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={next}
            data-testid={`${SLUG}-form-next`}
            className="h-12 min-h-[44px] px-5 text-[15px] font-semibold"
          >
            {step.key === "contact" ? "Review my answers" : "Continue"}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        )}

        <p className="text-[12.5px] leading-relaxed text-muted-foreground sm:ml-2">
          No obligation. We&apos;ll discuss your requirements before recommending a team
          structure and monthly fee.
        </p>
      </div>
    </div>
  );
};
